import { CONFIG } from './initState';
import ITEM_KEYS from './keys';
import { PRICING_STRATEGIES } from '../../resources/constants';
import { trimTranslationObject, trimTextTranslations } from 'utils/objectUtils';

export const toFormData = (product, categories) => {
  const categoriesMap = new Map();

  let config = product[ITEM_KEYS.CONFIG];

  for (const cat of product.categories) {
    if (categories.some(({ id }) => +id === +cat.category)) {
      if (!categoriesMap.has(cat.category)) categoriesMap.set(cat.category, []);
      continue;
    }
    const category = categories.find(({ subcategories }) => subcategories.some(({ id }) => +id === +cat.category));
    if (!!category) {
      if (categoriesMap.has(category.id)) {
        categoriesMap.set(category.id, [...categoriesMap.get(category.id), +cat.category]);
        continue;
      }
      categoriesMap.set(category.id, [+cat.category]);
    }
  }

  let variants = null;
  if( !!product[ITEM_KEYS.VARIANTS] ){
    variants = product[ITEM_KEYS.VARIANTS].map( variant => {
      let newVariant = {...variant};

      //Take obsolete MKT pricing strategy into config.
      if( config !== null && !!config.marketPrice && newVariant.pricingStrategy === PRICING_STRATEGIES.basePrice ) {
        newVariant.pricingStrategy = PRICING_STRATEGIES.mktPrice;
      }

      if( newVariant.pricingStrategy === PRICING_STRATEGIES.timeBasedPrice
        && newVariant.config !== null && newVariant.config.pricingStrategies !== null  ){

        for( const strategy in newVariant.config.pricingStrategies ){
          const currentStrategy = newVariant.config.pricingStrategies[strategy];

          if( !currentStrategy || !currentStrategy.rules || !Array.isArray(currentStrategy.rules)
              || !currentStrategy.rules.length > 0 ) continue;

          newVariant.config.pricingStrategies[strategy] = 
            getRulesByTimeBasedPrice(currentStrategy.rules, currentStrategy.inactives);
        }
      }

      return ({
        ...newVariant,
        [ITEM_KEYS.TRANSLATIONS]: trimTranslationObject(newVariant[ITEM_KEYS.TRANSLATIONS]),
      })
    });   
  }
  
  
  const processed = {
    ...product,
    [ITEM_KEYS.VARIANTS]: variants,
    [ITEM_KEYS.TRANSLATIONS]: trimTextTranslations(product[ITEM_KEYS.TRANSLATIONS]),
    [ITEM_KEYS.STOCK_TOTAL]: product[ITEM_KEYS.STOCK_TOTAL] === null ? 0 : product[ITEM_KEYS.STOCK_TOTAL],
    [ITEM_KEYS.STOCK_ALERT]: product[ITEM_KEYS.STOCK_ALERT] === null ? 0 : product[ITEM_KEYS.STOCK_ALERT],
    [ITEM_KEYS.STOCK_PAR]: product[ITEM_KEYS.STOCK_PAR] === null ? 0 : product[ITEM_KEYS.STOCK_PAR],
    [ITEM_KEYS.CONFIG]: product[ITEM_KEYS.CONFIG] === null ? {...CONFIG()} : {...CONFIG(), ...config},
    [ITEM_KEYS.CATEGORIES]: Array.from(categoriesMap.keys()).map((key) => ({
      category: key,
      subcategories: categoriesMap.get(key),
    })),
  };

  return processed;
};

export const toSubmitObject = (formData, oldCategories) => {

  let categories = formData[ITEM_KEYS.CATEGORIES]
    .filter(({ subcategories }) => subcategories.length === 0)
    .map(({ category }) => category);

  categories = [
    ...categories,
    ...formData[ITEM_KEYS.CATEGORIES]
      .filter(({ subcategories }) => subcategories.length > 0)
      .reduce((acc, curr) => [...acc, ...curr.subcategories], []),
  ];
  const variants = formData[ITEM_KEYS.VARIANTS].map( variant => {
    
    let pricingStrategies = !variant.config || !variant.config.pricingStrategies ? null : {...variant.config.pricingStrategies} 

    if(!!pricingStrategies && Object.keys(pricingStrategies).length > 0){
      for(const strategy in pricingStrategies){
        if( strategy === PRICING_STRATEGIES.timeBasedPrice && pricingStrategies[strategy] instanceof Map){              
          pricingStrategies = {
            ...pricingStrategies,
            [PRICING_STRATEGIES.timeBasedPrice]:
              getRulesByPriceWithSharedDays(Array.from(variant.config.pricingStrategies[strategy].values()), variant.price)
          };
        }
      } 
    }      
    return {
    ...variant,
    price: Number(variant.price),
    [ITEM_KEYS.TRANSLATIONS]: trimTranslationObject(variant[ITEM_KEYS.TRANSLATIONS]),
    config: {
      ...variant.config,
      pricingStrategies
    }
  }});

  const item = {
    ...formData,    
    [ITEM_KEYS.VARIANTS]: variants,
    [ITEM_KEYS.TRANSLATIONS]: trimTextTranslations(formData[ITEM_KEYS.TRANSLATIONS]),
    [ITEM_KEYS.CATEGORIES]: categories.map((cat) => ({ category: cat, position: getPositionIfExists(cat, oldCategories) })),
  };

  delete item[ITEM_KEYS.PRICE];
  // Delete legacy MKT config
  delete item[ITEM_KEYS.CONFIG].marketPrice;

  const image = formData[ITEM_KEYS.IMAGE];
  item[ITEM_KEYS.IMAGE] = image?.file ? image.file : image || "";

  const logo = formData[ITEM_KEYS.LOGO];
  item[ITEM_KEYS.LOGO] = logo?.file ? logo.file : logo || "";

  return item;
};

const getPositionIfExists = (catId, categories) => {
  if (!!categories && categories.some(({ category }) => +category === +catId)) {
    return categories.find(({ category }) => +category === +catId).position;
  }
  return null;
};

function getRulesByPriceWithSharedDays(rules, basePrice) {
  const priceMap = new Map();
  const inactives = new Set();

  for (const { timeRange, timeSpecificPrice, days, isActive } of rules) {
    const priceKey = `${timeSpecificPrice}`;

    if(!isActive){
      const key = `${timeRange.start}-${timeRange.end}-${timeSpecificPrice}-${basePrice}`;
      inactives.add(key);
    }

    if (!priceMap.has(priceKey)) {
      priceMap.set(priceKey, {
        timeSpecificPrice: Number(timeSpecificPrice),
        basePrice: Number(basePrice),
        schedule: [],
        dayMap: new Map()
      });
    }

    const group = priceMap.get(priceKey);

    for (const day of days) {
      if (!group.dayMap.has(day)) {
        group.dayMap.set(day, []);
      }
      group.dayMap.get(day).push({ start: timeRange.start, end: timeRange.end });
    }
  }

  // Finalize schedule: group by matched days
  for (const group of priceMap.values()) {
    const dayGroups = new Map();

    for (const [day, ranges] of group.dayMap.entries()) {
      const key = JSON.stringify(ranges);
      if (!dayGroups.has(key)) {
        dayGroups.set(key, { days: [], timeRanges: ranges });
      }
      dayGroups.get(key).days.push(day);
    }

    group.schedule = Array.from(dayGroups.values());
    delete group.dayMap;
  }

  return {
    rules: Array.from(priceMap.values()),
    inactives:inactives.size > 0 ?  Array.from(inactives) : null,
  };
}

const getRulesByTimeBasedPrice = ( rules, inactives = [] ) => {
  const resultMap = new Map();

  rules.forEach( ({ timeSpecificPrice, basePrice, schedule }) => {

    schedule.forEach(({ days, timeRanges }) => {      
      timeRanges.forEach(({ start, end }) => {
        const key = `${start}-${end}-${timeSpecificPrice}-${basePrice}`;
        if (!resultMap.has(key)) {
          resultMap.set(key, {
            timeRange: { start, end },
            timeSpecificPrice,
            basePrice,
            days: [...days],
            isActive: inactives === null || !inactives.includes(key) ? true : false,
          });
        } else {
          const existing = resultMap.get(key);
          existing.days.push(...days);
          existing.days = [...new Set(existing.days)];
        }
      });
    });
  });

  return resultMap;
}